import { useEffect } from 'react'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { AppRouter } from './router'
import { ErrorBoundary } from './components/errors/ErrorBoundary'
import { requestNotificationPermissions } from './lib/overdueNotifications'
import { initPushNotifications } from './lib/fcm'
import { isNativePlatform } from './lib/platform'
import { api } from './lib/api'
import { useAuthStore } from './stores/auth'

// Refresh enabled feature modules from the server whenever a session token is present.
// Covers every login path (login, role-select, impersonation, admin) and picks up
// admin toggles on the next app load / token change.
function ModulesBootstrap() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const setModules = useAuthStore((s) => s.setModules)

  useEffect(() => {
    if (!accessToken) return
    api.get('/auth/me')
      .then((r) => {
        if (r.data?.user?.modules) setModules(r.data.user.modules)
      })
      .catch(() => {})
  }, [accessToken, setModules])

  return null
}

// Handles Android hardware back button — navigate back instead of closing app
function BackButtonHandler() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!isNativePlatform()) return

    let cleanup: (() => void) | undefined

    import('@capacitor/app').then(({ App }) => {
      const listenerPromise = App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          navigate(-1)
        } else {
          App.minimizeApp()
        }
      })

      cleanup = () => {
        listenerPromise.then((l) => l.remove()).catch(() => {})
      }
    }).catch(() => {})

    return () => cleanup?.()
  }, [navigate])

  return null
}

export default function App() {
  // Request notification permissions and register FCM on first launch (native only)
  useEffect(() => {
    requestNotificationPermissions().catch(() => {})
    initPushNotifications().catch(() => {})
  }, [])

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <BackButtonHandler />
        <ModulesBootstrap />
        <AppRouter />
      </BrowserRouter>
    </ErrorBoundary>
  )
}
